
import { Component, Watch, Mixins } from 'vue-property-decorator';
import ECommerceTable from '@/features/ecommerce/components/ECommerceTable.vue';
import { StoreForImport } from '@/features/shipment/types';
import AddStoreModal from '@/features/ecommerce/components/AddStoreModal.vue';
import InstalledModal from '@/features/ecommerce/components/InstalledModal.vue';
import StoreDeletionModal from '@/features/ecommerce/components/StoreDeletionModal.vue';
import {
  getConnectedStores,
  getSupportedStores,
  addStoreConnection,
  deleteStoreConnection,
  AddStoreResponse,
} from '@/features/ecommerce/services/e-commerce.services';
import { showErrorAlertDialog } from '@/features/common/helpers';
import { GlobalResponseType } from '@/features/common/types';
import { PreferencesMixin } from '@/features/settings/components/preferences/content/mixins';
import { patchPreferences } from '@/features/settings/services/preferences.services';
import { AddStorePayload, SupportedIntegration } from '../types';

export enum StoreDeletionSteps {
  CONFIRM = 'CONFIRM',
  COMPLETED = 'COMPLETED'
}

@Component({
  components: {
    ECommerceTable,
    AddStoreModal,
    InstalledModal,
    StoreDeletionModal,
  },
})
export default class ECommerce extends Mixins(PreferencesMixin) {
  private isLoading = false;

  private showAddStoreModal = false;

  private showInstalledModal = true;

  private showFailedModal = false;

  private showStoreDeletionModal = false;

  private supportedStoresLoading = false;

  private addStoreLoading = false;

  private deleteStoreLoading = false;

  private storeDeletionStep = '';

  private storeIdToDelete = '';

  private connectedStores: StoreForImport[] = []

  private supportedStores: SupportedIntegration[] = []

  get forceShowInstallationModalQuery(): string {
    return this.$route.query.success as string;
  }

  get sortedSupportedStores(): SupportedIntegration[] {
    return this.supportedStores
      .sort((store1, store2) => store1.platformType.localeCompare(store2.platformType));
  }

  @Watch('forceShowInstallationModalQuery', { immediate: true })
  private forceShowInstallationModal(newVal: string): void {
    this.showInstalledModal = newVal === 'true';
    this.showFailedModal = newVal === 'false';
  }

  async created(): Promise<void> {
    await this.fetchConnectedStores();

    if (this.showInstalledModal && this.connectedStores?.length === 1) {
      const [store] = this.connectedStores;

      this.setStoreAsDefault({
        id: store.id,
        name: store.shopDetails.name,
        url: store.shopDetails.url,
      });
    }
  }

  private async fetchConnectedStores(isLoading = true): Promise<void> {
    if (isLoading) {
      this.isLoading = true;
    }

    const stores = await getConnectedStores();

    if (!stores) {
      showErrorAlertDialog();
    }

    this.connectedStores = stores as StoreForImport[] || [];
    this.isLoading = false;
  }

  private async fetchSupportedStores(): Promise<void> {
    this.supportedStoresLoading = true;
    const stores = await getSupportedStores();

    if (!stores) {
      showErrorAlertDialog();
    }

    this.supportedStores = stores as SupportedIntegration[] || [];
    this.supportedStoresLoading = false;
  }

  private async addStoreClickHandler(): Promise<void> {
    await this.fetchSupportedStores();
    this.showAddStoreModal = true;
  }

  private async addStore(payload: AddStorePayload): Promise<void> {
    this.addStoreLoading = true;
    const res = await addStoreConnection(payload);

    if (!res || (res as GlobalResponseType).successful === false
    ) {
      showErrorAlertDialog((res as GlobalResponseType)?.errorMessage);
      this.addStoreLoading = false;
      return;
    }

    const { redirectTo, platformConnectorList } = res as AddStoreResponse;

    if (redirectTo) {
      window.location.replace(redirectTo);
      return;
    }

    if (platformConnectorList?.length === 1) {
      const [store] = platformConnectorList;

      await this.setStoreAsDefault({
        id: store.id,
        name: store.shopDetails.name,
        url: store.shopDetails.url,
      });
    }

    this.addStoreLoading = false;
    this.showAddStoreModal = false;
    this.showInstalledModal = true;

    this.$store.commit('setStoresForImport', platformConnectorList);
    this.connectedStores = platformConnectorList as StoreForImport[];
  }

  private deleteStoreClickHandler(id: string): void {
    this.storeIdToDelete = id;
    this.storeDeletionStep = StoreDeletionSteps.CONFIRM;
    this.showStoreDeletionModal = true;
  }

  private async deleteStore(): Promise<void> {
    if (this.storeDeletionStep === StoreDeletionSteps.COMPLETED) {
      this.showStoreDeletionModal = false;
      return;
    }

    this.deleteStoreLoading = true;

    const isSuccess = await deleteStoreConnection(this.storeIdToDelete);
    await this.fetchConnectedStores(false);

    this.$store.commit('setStoresForImport', this.connectedStores);
    this.deleteStoreLoading = false;

    if (!isSuccess) {
      showErrorAlertDialog();
      return;
    }

    this.storeDeletionStep = StoreDeletionSteps.COMPLETED;
  }

  private async setStoreAsDefault(shopDetails: {
    id: number,
    name: string,
    url: string
  }): Promise<void> {
    const path = 'ecommerce/defaultStore';

    this.patchPreferenceInStore(path, shopDetails);

    await patchPreferences(this.userPreferences.id, [
      {
        path,
        op: 'replace',
        value: shopDetails,
      },
    ]);
  }
}
